import { useEffect, useMemo, useState } from 'react';
import { marketingWebAPI } from '../../api/api';
import {
  RefreshCw, Link2, AlertTriangle, CheckCircle2, XCircle,
  ShieldQuestion, ExternalLink, Plus, Pencil, Trash2, Eye,
  Search, Download, Upload, Euro, Award, Hash, Sparkles, Settings, X as XIcon,
  Target, TrendingUp,
} from 'lucide-react';
import {
  Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';

ChartJS.register(ArcElement, ChartTooltip, Legend);

/**
 * /marketing/web/backlinks/ — Tracking de enlaces SEO comprados.
 *
 * F1-F2: CRUD + verificacion HTTP.
 * F3: frontend (esta pagina).
 * F4: cron diario 05:15 + Telegram consolidado.
 * F5: cruce GSC + sugerencias automaticas con anchor y fragmento.
 * F6: plan mensual editable + donut de distribucion de anchors.
 */

const ESTADOS = [
  { value: 'planificado', label: 'Planificado', color: 'bg-gray-100 text-gray-700' },
  { value: 'contratado', label: 'Contratado', color: 'bg-blue-100 text-blue-700' },
  { value: 'publicado', label: 'Publicado', color: 'bg-green-100 text-green-700' },
  { value: 'indexado', label: 'Indexado', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'alterado', label: 'Alterado', color: 'bg-amber-100 text-amber-700' },
  { value: 'caido', label: 'Caído', color: 'bg-red-100 text-red-700' },
  { value: 'verificacion_bloqueada', label: 'Anti-bot 403', color: 'bg-purple-100 text-purple-700' },
  { value: 'perdido', label: 'Perdido', color: 'bg-rose-100 text-rose-700' },
];

const TIPOS_ANCHOR = [
  { value: 'exact_match', label: 'Exact match' },
  { value: 'partial', label: 'Partial' },
  { value: 'brand', label: 'Brand' },
  { value: 'url', label: 'URL desnuda' },
  { value: 'generico', label: 'Genérico' },
  { value: 'imagen', label: 'Imagen' },
];

const PLATAFORMAS = [
  { value: 'linkatomic', label: 'Linkatomic' },
  { value: 'publisuites', label: 'Publisuites' },
  { value: 'prensarank', label: 'Prensarank' },
  { value: 'coobis', label: 'Coobis' },
  { value: 'unancor', label: 'Unancor' },
  { value: 'growwer', label: 'Growwer' },
  { value: 'manual', label: 'Manual (contacto directo)' },
  { value: 'ganado', label: 'Ganado (no comprado)' },
];

const fmtEur = (n) => (n == null ? '—' : `${Number(n).toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`);
const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString('es-ES'));
const fmtPct = (v) => (v == null ? '—' : `${(v * 100).toFixed(0)} %`);

function EstadoBadge({ value }) {
  const e = ESTADOS.find((x) => x.value === value) || { label: value, color: 'bg-gray-100 text-gray-700' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${e.color}`}>
      {e.label}
    </span>
  );
}

function KpiCard({ label, value, icon: Icon, hint, tone = 'neutral' }) {
  const toneClass = {
    neutral: '',
    warn: 'border-amber-300 bg-amber-50',
    ok: 'border-emerald-300 bg-emerald-50',
    bad: 'border-red-300 bg-red-50',
  }[tone];
  return (
    <div className={`bg-white border rounded-lg p-4 ${toneClass}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs uppercase tracking-wide font-semibold text-gray-500">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}

function mesActualISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function rellenaMesOpciones() {
  // Últimos 12 meses + 1 futuro (por si planificas adelantado)
  const out = [];
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  for (let i = 0; i < 14; i++) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

// Datos COMPARTIDOS entre todos los enlaces de un mismo artículo
// (Linkatomic vende artículo con N enlaces; estos campos no varían).
const COMPARTIDO_VACIO = {
  dominio: '', url_post: '', fecha_publicacion: '',
  dr_ahrefs: '', da_moz: '', tf_majestic: '', trafico_mensual_estimado: '',
  plataforma: 'linkatomic', fecha_compra: '',
  precio_total_eur: '',  // del artículo entero; se reparte entre N enlaces
  notas_articulo: '',
};

// Datos por ENLACE (cada uno es un backlink en BD).
const ENLACE_VACIO = {
  anchor_text: '', tipo_anchor: 'exact_match',
  target_url: '', dofollow: true,
  kw_objetivo: '', estado: 'contratado', notas: '',
};

function BacklinkFormDialog({ open, onClose, onSaved, initial, prefill }) {
  const editing = !!initial?.id;
  // En modo creación gestionamos N enlaces. En modo edición SOLO 1 (no
  // permitimos añadir más a un artículo ya creado; si quieres más, pulsa
  // "Nuevo backlink" desde la tabla y rellena dominio/url_post igual).
  const [compartido, setCompartido] = useState(COMPARTIDO_VACIO);
  const [enlaces, setEnlaces] = useState([{ ...ENLACE_VACIO }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      // Modo edición: 1 enlace, datos rellenados del backlink existente.
      setCompartido({
        ...COMPARTIDO_VACIO,
        dominio: initial.dominio || '',
        url_post: initial.url_post || '',
        fecha_publicacion: initial.fecha_publicacion || '',
        dr_ahrefs: initial.dr_ahrefs ?? '',
        da_moz: initial.da_moz ?? '',
        tf_majestic: initial.tf_majestic ?? '',
        trafico_mensual_estimado: initial.trafico_mensual_estimado ?? '',
        plataforma: initial.plataforma || 'linkatomic',
        fecha_compra: initial.fecha_compra || '',
        precio_total_eur: initial.precio_eur ?? '',  // edición: tratamos como del único enlace
        notas_articulo: '',
      });
      setEnlaces([{
        anchor_text: initial.anchor_text || '',
        tipo_anchor: initial.tipo_anchor || 'exact_match',
        target_url: initial.target_url || '',
        dofollow: initial.dofollow !== false,
        kw_objetivo: initial.kw_objetivo || '',
        estado: initial.estado || 'contratado',
        notas: initial.notas || '',
      }]);
    } else if (prefill) {
      // Vienes de "Crear desde sugerencia": el dominio/url los rellena el
      // usuario porque dependen de la plataforma; rellenamos lo deducible
      // del análisis GSC: anchor, target, KW objetivo, plataforma sugerida,
      // precio aproximado y notas con el fragmento sugerido.
      setCompartido({
        ...COMPARTIDO_VACIO,
        fecha_compra: new Date().toISOString().slice(0, 10),
        plataforma: prefill.plataforma || 'linkatomic',
        precio_total_eur: prefill.precio_total_eur ?? '',
        notas_articulo: prefill.notas_articulo || '',
      });
      setEnlaces([{
        ...ENLACE_VACIO,
        anchor_text: prefill.anchor_text || '',
        tipo_anchor: prefill.tipo_anchor || 'exact_match',
        target_url: prefill.target_url || '',
        kw_objetivo: prefill.kw_objetivo || '',
      }]);
    } else {
      setCompartido({ ...COMPARTIDO_VACIO, fecha_compra: new Date().toISOString().slice(0, 10) });
      setEnlaces([{ ...ENLACE_VACIO }]);
    }
    setError(null);
  }, [open, initial, prefill]);

  const setC = (k, v) => setCompartido((s) => ({ ...s, [k]: v }));
  const setE = (idx, k, v) => setEnlaces((arr) => arr.map((e, i) => (i === idx ? { ...e, [k]: v } : e)));
  const addEnlace = () => setEnlaces((arr) => [...arr, { ...ENLACE_VACIO }]);
  const removeEnlace = (idx) => setEnlaces((arr) => (arr.length > 1 ? arr.filter((_, i) => i !== idx) : arr));
  const numOrNull = (v) => (v === '' || v == null ? null : Number(v));

  // Precio por enlace: reparto automático del total entre N.
  const precioPorEnlace = useMemo(() => {
    const total = numOrNull(compartido.precio_total_eur);
    if (total == null) return 0;
    return Math.round((total / enlaces.length) * 100) / 100;
  }, [compartido.precio_total_eur, enlaces.length]);

  const submit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError(null);
    const baseCompartido = {
      dominio: compartido.dominio,
      url_post: compartido.url_post,
      fecha_publicacion: compartido.fecha_publicacion || null,
      dr_ahrefs: numOrNull(compartido.dr_ahrefs),
      da_moz: numOrNull(compartido.da_moz),
      tf_majestic: numOrNull(compartido.tf_majestic),
      trafico_mensual_estimado: numOrNull(compartido.trafico_mensual_estimado),
      plataforma: compartido.plataforma,
      fecha_compra: compartido.fecha_compra || null,
    };

    try {
      if (editing) {
        // Edición: 1 solo enlace. PUT con TODOS los campos editables (incluidos
        // origen, plataforma, fecha_compra y métricas DR/DA) por si hay que
        // corregir un error de alta o rellenar algo que faltaba.
        const enl = enlaces[0];
        await marketingWebAPI.actualizarBacklink(initial.id, {
          dominio: compartido.dominio || undefined,
          url_post: compartido.url_post || undefined,
          fecha_publicacion: compartido.fecha_publicacion || null,
          plataforma: compartido.plataforma || undefined,
          fecha_compra: compartido.fecha_compra || null,
          dr_ahrefs: numOrNull(compartido.dr_ahrefs),
          da_moz: numOrNull(compartido.da_moz),
          tf_majestic: numOrNull(compartido.tf_majestic),
          trafico_mensual_estimado: numOrNull(compartido.trafico_mensual_estimado),
          anchor_text: enl.anchor_text,
          tipo_anchor: enl.tipo_anchor,
          target_url: enl.target_url,
          dofollow: enl.dofollow,
          estado: enl.estado,
          notas: enl.notas,
          kw_objetivo: enl.kw_objetivo,
          precio_eur: numOrNull(compartido.precio_total_eur) ?? 0,
        });
      } else {
        // Creación: N POSTs en serie con precio_eur repartido. Si uno falla,
        // paramos y reportamos qué enlaces sí entraron y qué fallaron.
        const exitos = [];
        const fallos = [];
        for (let i = 0; i < enlaces.length; i++) {
          const enl = enlaces[i];
          const payload = {
            ...baseCompartido,
            ...enl,
            precio_eur: precioPorEnlace,
            // notas individuales prevalecen; si están vacías usamos las del artículo
            notas: enl.notas || compartido.notas_articulo || '',
          };
          try {
            await marketingWebAPI.crearBacklink(payload);
            exitos.push(i + 1);
          } catch (err) {
            const detail = err?.response?.data?.detail || 'Error desconocido';
            fallos.push({ idx: i + 1, anchor: enl.anchor_text || '(sin anchor)', detail });
          }
        }
        if (fallos.length > 0) {
          const resumen = fallos
            .map((f) => `· Enlace #${f.idx} "${f.anchor}": ${f.detail}`)
            .join('\n');
          const cabecera = exitos.length > 0
            ? `${exitos.length} de ${enlaces.length} enlaces creados. Fallos:\n${resumen}`
            : `Ningún enlace se creó. Errores:\n${resumen}`;
          setError(cabecera);
          if (exitos.length > 0) onSaved?.();  // refrescar tabla con los que sí entraron
          return;
        }
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error('Error guardando backlink:', err);
      setError(err?.response?.data?.detail || 'Error guardando');
    } finally {
      setSaving(false);
    }
  };

  const titulo = editing
    ? 'Editar backlink'
    : enlaces.length > 1
      ? `Nuevo artículo con ${enlaces.length} enlaces`
      : 'Nuevo backlink';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          <DialogDescription>
            {editing
              ? 'Puedes editar todos los campos a mano (origen, métricas DR/DA, plataforma, anchor, target, estado…) por si hay que corregir un error o rellenar algo que faltaba.'
              : 'Registra un artículo y sus enlaces. Si en el mismo artículo te dan 2 dofollow (típico Linkatomic), pulsa "+ Otro enlace" abajo. El precio total se reparte automáticamente.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          {/* Origen del artículo (compartido). Editable también en edición
              para corregir un dominio/URL mal tecleados. */}
          <fieldset className="border rounded-md p-3">
            <legend className="text-xs font-semibold text-gray-500 px-1">Artículo (datos compartidos)</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Campo label="Dominio *" hint="ej. restauracionnews.com">
                <input required value={compartido.dominio}
                  onChange={(e) => setC('dominio', e.target.value.toLowerCase().trim())}
                  className="w-full px-3 py-2 text-sm border rounded-md" placeholder="dominio.com" />
              </Campo>
              <Campo label="URL del artículo *" hint="URL completa del post">
                <input required type="url" value={compartido.url_post}
                  onChange={(e) => setC('url_post', e.target.value.trim())}
                  className="w-full px-3 py-2 text-sm border rounded-md" placeholder="https://..." />
              </Campo>
              <Campo label="Fecha publicación" hint="del artículo (no de la compra)">
                <input type="date" value={compartido.fecha_publicacion || ''}
                  onChange={(e) => setC('fecha_publicacion', e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-md" />
              </Campo>
            </div>
          </fieldset>

          {/* Métricas dominio (compartido). Editables a mano también en edición:
              en enlaces antiguos rara vez se capturaron al comprar. */}
          <fieldset className="border rounded-md p-3">
            <legend className="text-xs font-semibold text-gray-500 px-1">
              Métricas del dominio{editing ? ' (editables a mano)' : ' (momento de compra)'}
            </legend>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Campo label="DR Ahrefs" hint="0-100">
                <input type="number" min="0" max="100" value={compartido.dr_ahrefs}
                  onChange={(e) => setC('dr_ahrefs', e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-md" />
              </Campo>
              <Campo label="DA Moz" hint="0-100">
                <input type="number" min="0" max="100" value={compartido.da_moz}
                  onChange={(e) => setC('da_moz', e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-md" />
              </Campo>
              <Campo label="TF Majestic" hint="0-100">
                <input type="number" min="0" max="100" value={compartido.tf_majestic}
                  onChange={(e) => setC('tf_majestic', e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-md" />
              </Campo>
              <Campo label="Tráfico/mes" hint="estimado">
                <input type="number" min="0" value={compartido.trafico_mensual_estimado}
                  onChange={(e) => setC('trafico_mensual_estimado', e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-md" />
              </Campo>
            </div>
          </fieldset>

          {/* Compra (compartido) */}
          <fieldset className="border rounded-md p-3">
            <legend className="text-xs font-semibold text-gray-500 px-1">Compra del artículo</legend>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Campo label="Plataforma *">
                <select value={compartido.plataforma} onChange={(e) => setC('plataforma', e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-md">
                  {PLATAFORMAS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </Campo>
              <Campo
                label={editing ? 'Precio (€)' : `Precio total (€) — reparto: ${fmtEur(precioPorEnlace)} × ${enlaces.length}`}
                hint={editing ? null : 'Se reparte automáticamente entre los enlaces'}>
                <input type="number" step="0.01" min="0" value={compartido.precio_total_eur}
                  onChange={(e) => setC('precio_total_eur', e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-md" placeholder="140.00" />
              </Campo>
              <Campo label="Fecha compra">
                <input type="date" value={compartido.fecha_compra || ''}
                  onChange={(e) => setC('fecha_compra', e.target.value)}
                  className="w-full px-3 py-2 text-sm border rounded-md" />
              </Campo>
            </div>
          </fieldset>

          {/* Enlaces (array) */}
          <fieldset className="border rounded-md p-3">
            <legend className="text-xs font-semibold text-gray-500 px-1">
              Enlaces en el artículo ({enlaces.length})
            </legend>
            <div className="space-y-3">
              {enlaces.map((enl, idx) => (
                <div key={idx} className="border rounded-md p-3 bg-gray-50 relative">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-600">
                      Enlace #{idx + 1}
                    </span>
                    {!editing && enlaces.length > 1 && (
                      <button type="button" onClick={() => removeEnlace(idx)}
                        className="text-red-600 hover:bg-red-50 p-1 rounded" title="Quitar este enlace">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Campo label="Anchor text *" hint="texto visible del link">
                      <input required value={enl.anchor_text}
                        onChange={(e) => setE(idx, 'anchor_text', e.target.value)}
                        className="w-full px-3 py-2 text-sm border rounded-md bg-white"
                        placeholder="catering corporativo madrid" />
                    </Campo>
                    <Campo label="Tipo de anchor *">
                      <select value={enl.tipo_anchor}
                        onChange={(e) => setE(idx, 'tipo_anchor', e.target.value)}
                        className="w-full px-3 py-2 text-sm border rounded-md bg-white">
                        {TIPOS_ANCHOR.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </Campo>
                    <Campo label="Target URL *" hint="página propia que recibe el link">
                      <input required type="url" value={enl.target_url}
                        onChange={(e) => setE(idx, 'target_url', e.target.value.trim())}
                        className="w-full px-3 py-2 text-sm border rounded-md bg-white"
                        placeholder="https://duettocatering.com/..." />
                    </Campo>
                    <Campo label="KW objetivo" hint="KW que queremos empujar (cruce GSC)">
                      <input value={enl.kw_objetivo}
                        onChange={(e) => setE(idx, 'kw_objetivo', e.target.value)}
                        className="w-full px-3 py-2 text-sm border rounded-md bg-white"
                        placeholder="catering corporativo madrid" />
                    </Campo>
                    <Campo label="Estado">
                      <select value={enl.estado}
                        onChange={(e) => setE(idx, 'estado', e.target.value)}
                        className="w-full px-3 py-2 text-sm border rounded-md bg-white">
                        {ESTADOS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </Campo>
                    <Campo label="Dofollow">
                      <label className="inline-flex items-center gap-2 text-sm pt-2">
                        <input type="checkbox" checked={enl.dofollow}
                          onChange={(e) => setE(idx, 'dofollow', e.target.checked)} />
                        <span>Es dofollow (sin nofollow/sponsored/ugc)</span>
                      </label>
                    </Campo>
                  </div>
                  <div className="mt-3">
                    <Campo label="Notas (de este enlace)">
                      <textarea value={enl.notas}
                        onChange={(e) => setE(idx, 'notas', e.target.value)} rows={2}
                        className="w-full px-3 py-2 text-sm border rounded-md bg-white"
                        placeholder="Observaciones específicas de este enlace…" />
                    </Campo>
                  </div>
                </div>
              ))}

              {!editing && (
                <button type="button" onClick={addEnlace}
                  className="w-full px-3 py-2 text-sm border border-dashed border-gray-300 rounded-md hover:bg-gray-50 text-gray-600 flex items-center justify-center gap-2">
                  <Plus className="w-4 h-4" />
                  Otro enlace en este artículo
                </button>
              )}
            </div>
          </fieldset>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800 whitespace-pre-wrap">
              {error}
            </div>
          )}

          <DialogFooter>
            <button type="button" onClick={onClose} className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50">
              Cancelar
            </button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm bg-duetto-primary text-white rounded-md hover:opacity-90 disabled:opacity-50">
              {saving
                ? 'Guardando…'
                : editing
                  ? 'Guardar cambios'
                  : enlaces.length > 1
                    ? `Crear ${enlaces.length} backlinks`
                    : 'Crear backlink'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Campo({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-gray-400 mt-0.5">{hint}</span>}
    </label>
  );
}

function DetalleDialog({ backlink, onClose, onVerHistorial }) {
  if (!backlink) return null;
  const verificaciones = backlink.verificaciones || [];

  return (
    <Dialog open={!!backlink} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-duetto-primary" />
            {backlink.dominio}
          </DialogTitle>
          <DialogDescription>
            <a href={backlink.url_post} target="_blank" rel="noopener noreferrer"
              className="text-blue-600 hover:underline inline-flex items-center gap-1 text-xs font-mono break-all">
              {backlink.url_post}
              <ExternalLink className="w-3 h-3 shrink-0" />
            </a>
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 text-sm">
          <Info label="Anchor" valor={`"${backlink.anchor_text}"`} />
          <Info label="Tipo anchor" valor={TIPOS_ANCHOR.find((t) => t.value === backlink.tipo_anchor)?.label || backlink.tipo_anchor} />
          <Info label="Target URL" valor={backlink.target_url} mono />
          <Info label="Dofollow" valor={backlink.dofollow ? 'Sí' : 'No (nofollow / sponsored)'} />
          <Info label="DR / DA / TF" valor={`${backlink.dr_ahrefs ?? '—'} / ${backlink.da_moz ?? '—'} / ${backlink.tf_majestic ?? '—'}`} />
          <Info label="Tráfico/mes" valor={fmtNum(backlink.trafico_mensual_estimado)} />
          <Info label="Plataforma" valor={PLATAFORMAS.find((p) => p.value === backlink.plataforma)?.label || backlink.plataforma} />
          <Info label="Precio" valor={fmtEur(backlink.precio_eur)} />
          <Info label="Fecha compra" valor={backlink.fecha_compra || '—'} />
          <Info label="Fecha publicación" valor={backlink.fecha_publicacion || '—'} />
          <Info label="KW objetivo" valor={backlink.kw_objetivo || '—'} />
          <Info label="Estado" valor={<EstadoBadge value={backlink.estado} />} />
        </div>

        {backlink.notas && (
          <div className="mt-4 bg-gray-50 border rounded-md p-3 text-sm">
            <span className="text-xs font-semibold text-gray-500 uppercase">Notas</span>
            <div className="mt-1 whitespace-pre-wrap">{backlink.notas}</div>
          </div>
        )}

        <div className="mt-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Verificaciones (últimas {verificaciones.length})</h3>
          {verificaciones.length === 0 ? (
            <div className="text-xs text-gray-500 bg-gray-50 border rounded-md p-3">
              Sin verificaciones aún. Pulsa "Verificar" en la tabla o espera al cron diario (05:15 Madrid) para tener histórico.
              <br />
              <span className="text-gray-400 italic">(la verificación HTTP automática llega en Fase 2)</span>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                  <TableHead>Anchor detectado</TableHead>
                  <TableHead>rel</TableHead>
                  <TableHead className="text-right">Tiempo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {verificaciones.slice().reverse().map((v, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs">{new Date(v.fecha).toLocaleString('es-ES')}</TableCell>
                    <TableCell className="text-right text-xs">{v.status_code}</TableCell>
                    <TableCell className="text-xs">{v.anchor_detectado || '—'}</TableCell>
                    <TableCell className="text-xs">{v.rel_attr || '—'}</TableCell>
                    <TableCell className="text-right text-xs">{v.response_time_ms?.toFixed(0)} ms</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <DialogFooter className="mt-4">
          {backlink.kw_objetivo && (
            <button onClick={onVerHistorial}
              className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-600" />
              Ver historial GSC
            </button>
          )}
          <button onClick={onClose} className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50">Cerrar</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, valor, mono }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide font-semibold text-gray-500">{label}</div>
      <div className={`text-sm text-gray-900 ${mono ? 'font-mono break-all' : ''}`}>{valor}</div>
    </div>
  );
}

// Orden de columnas esperado al pegar la tabla (desde Excel/Sheets, separado
// por tabuladores). Solo dominio, url_post, anchor_text, tipo_anchor,
// target_url y plataforma son obligatorios; el resto es opcional.
const IMPORT_COLS = [
  'dominio', 'url_post', 'anchor_text', 'tipo_anchor', 'target_url',
  'dofollow', 'plataforma', 'precio_eur', 'fecha_compra', 'fecha_publicacion',
  'kw_objetivo', 'dr_ahrefs', 'da_moz', 'estado',
];
const TIPOS_VALIDOS = TIPOS_ANCHOR.map((t) => t.value);
const PLATAFORMAS_VALIDAS = PLATAFORMAS.map((p) => p.value);
const ESTADOS_VALIDOS = ESTADOS.map((e) => e.value);

function parseDofollow(v) {
  if (v == null || v === '') return true;
  const s = String(v).toLowerCase().trim();
  return !['no', 'nofollow', 'false', '0', 'n'].includes(s);
}
function numOrNullImport(v) {
  if (v == null || String(v).trim() === '') return null;
  const n = Number(String(v).replace(',', '.'));
  return Number.isNaN(n) ? null : n;
}

function parsearFilasImport(texto) {
  const filas = [];
  const errores = [];
  const lineas = texto.split('\n').map((l) => l.replace(/\r$/, '')).filter((l) => l.trim() !== '');
  if (lineas.length === 0) return { filas, errores };
  // Detectar separador: tabulador (paste de hoja de cálculo) o coma.
  const sep = lineas.some((l) => l.includes('\t')) ? '\t' : ',';
  lineas.forEach((linea, i) => {
    const celdas = linea.split(sep).map((c) => c.trim());
    // Saltar fila de cabecera si la primera celda es "dominio"
    if (i === 0 && celdas[0]?.toLowerCase() === 'dominio') return;
    const row = {};
    IMPORT_COLS.forEach((col, idx) => { row[col] = celdas[idx] ?? ''; });

    const faltan = [];
    if (!row.dominio) faltan.push('dominio');
    if (!row.url_post) faltan.push('url_post');
    if (!row.anchor_text) faltan.push('anchor_text');
    if (!row.target_url) faltan.push('target_url');
    if (faltan.length) {
      errores.push(`Fila ${i + 1}: faltan campos (${faltan.join(', ')})`);
      return;
    }
    const tipo = TIPOS_VALIDOS.includes(row.tipo_anchor) ? row.tipo_anchor : 'partial';
    const plat = PLATAFORMAS_VALIDAS.includes(row.plataforma) ? row.plataforma : 'manual';
    const est = ESTADOS_VALIDOS.includes(row.estado) ? row.estado : 'publicado';
    filas.push({
      dominio: row.dominio.toLowerCase(),
      url_post: row.url_post,
      anchor_text: row.anchor_text,
      tipo_anchor: tipo,
      target_url: row.target_url,
      dofollow: parseDofollow(row.dofollow),
      plataforma: plat,
      precio_eur: numOrNullImport(row.precio_eur) ?? 0,
      fecha_compra: row.fecha_compra || null,
      fecha_publicacion: row.fecha_publicacion || null,
      kw_objetivo: row.kw_objetivo || null,
      dr_ahrefs: numOrNullImport(row.dr_ahrefs),
      da_moz: numOrNullImport(row.da_moz),
      estado: est,
    });
  });
  return { filas, errores };
}

function ImportarDialog({ open, onClose, onImported }) {
  const [texto, setTexto] = useState('');
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) { setTexto(''); setResultado(null); setError(null); }
  }, [open]);

  const { filas, errores } = useMemo(() => parsearFilasImport(texto), [texto]);

  const submit = async () => {
    if (filas.length === 0) { setError('No hay filas válidas que importar'); return; }
    setImportando(true);
    setError(null);
    try {
      const res = await marketingWebAPI.importarBacklinks(filas);
      setResultado(res.data);
      onImported?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error importando');
    } finally {
      setImportando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle>Importación masiva de backlinks</DialogTitle>
          <DialogDescription>
            Pega una tabla (desde Excel/Sheets, separada por tabuladores) con una fila por enlace.
            Sirve para cargar de golpe enlaces antiguos. Los duplicados (mismo dominio + url + target) se omiten.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 text-xs bg-gray-50 border rounded-md p-3 text-gray-600">
          <strong>Orden de columnas</strong> (solo las 4 primeras + plataforma son obligatorias):
          <code className="block mt-1 font-mono text-[11px] break-all">
            dominio · url_post · anchor_text · tipo_anchor · target_url · dofollow(si/no) · plataforma · precio_eur · fecha_compra · fecha_publicacion · kw_objetivo · dr_ahrefs · da_moz · estado
          </code>
          <span className="block mt-1 text-[11px] text-gray-400">
            tipo_anchor: {TIPOS_VALIDOS.join(', ')} · plataforma: {PLATAFORMAS_VALIDAS.join(', ')} · estado por defecto: publicado
          </span>
        </div>

        <textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          rows={10}
          placeholder={'elconfidencialdigital.com\thttps://...\tDuetto, catering para empresas en Madrid\tpartial\thttps://duettocatering.com/empresas/\tsi\tlinkatomic\t330\t2026-05-29\t2026-05-29\tcatering para empresas madrid\t\t\tpublicado'}
          className="w-full mt-3 px-3 py-2 text-xs font-mono border rounded-md"
        />

        {texto.trim() !== '' && (
          <div className="mt-2 text-sm">
            <span className="text-green-700 font-medium">{filas.length} fila(s) válida(s)</span>
            {errores.length > 0 && (
              <div className="mt-1 bg-amber-50 border border-amber-200 rounded-md p-2 text-xs text-amber-800 whitespace-pre-wrap">
                {errores.join('\n')}
              </div>
            )}
            {filas.length > 0 && (
              <div className="mt-2 max-h-40 overflow-y-auto border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Dominio</TableHead>
                      <TableHead>Anchor</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-right">€</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filas.map((f, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs">{f.dominio}</TableCell>
                        <TableCell className="text-xs">"{f.anchor_text}"</TableCell>
                        <TableCell className="text-xs font-mono">{(f.target_url || '').replace('https://duettocatering.com', '') || '/'}</TableCell>
                        <TableCell className="text-xs">{f.tipo_anchor}{!f.dofollow && ' · nofollow'}</TableCell>
                        <TableCell className="text-right text-xs">{fmtEur(f.precio_eur)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {resultado && (
          <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm text-emerald-800">
            Importación: <strong>{resultado.creados}</strong> creados, {resultado.omitidos} omitidos (duplicados), {resultado.errores} con error.
            {resultado.detalle_omitidos?.length > 0 && (
              <ul className="mt-1 text-[11px] list-disc pl-4">
                {resultado.detalle_omitidos.map((o, i) => <li key={i}>{o.dominio} → {o.target_url}: {o.motivo}</li>)}
              </ul>
            )}
          </div>
        )}
        {error && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800 whitespace-pre-wrap">{error}</div>
        )}

        <DialogFooter className="mt-4">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50">
            {resultado ? 'Cerrar' : 'Cancelar'}
          </button>
          {!resultado && (
            <button type="button" onClick={submit} disabled={importando || filas.length === 0}
              className="px-4 py-2 text-sm bg-duetto-primary text-white rounded-md hover:opacity-90 disabled:opacity-50">
              {importando ? 'Importando…' : `Importar ${filas.length || ''} backlink(s)`}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MarketingWebBacklinks() {
  const [items, setItems] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [sugerencias, setSugerencias] = useState(null);
  const [loadingSug, setLoadingSug] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mes, setMes] = useState(mesActualISO());
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroPlataforma, setFiltroPlataforma] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [incluirInactivos, setIncluirInactivos] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [prefillSugerencia, setPrefillSugerencia] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [historialGsc, setHistorialGsc] = useState(null);
  const [campanas, setCampanas] = useState(null);
  const [loadingCampanas, setLoadingCampanas] = useState(true);
  const [verificando, setVerificando] = useState(null);
  const [verifAllRunning, setVerifAllRunning] = useState(false);
  const [planMensualOpen, setPlanMensualOpen] = useState(false);
  const [toast, setToast] = useState(null); // {tipo, msg}

  const mostrarToast = (tipo, msg) => {
    setToast({ tipo, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const cargar = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { mes, incluir_inactivos: incluirInactivos };
      if (filtroEstado) params.estado = filtroEstado;
      if (filtroPlataforma) params.plataforma = filtroPlataforma;
      const [resList, resKpis] = await Promise.all([
        marketingWebAPI.listarBacklinks(params),
        marketingWebAPI.kpisBacklinks(mes),
      ]);
      setItems(resList.data?.items || []);
      setKpis(resKpis.data);
    } catch (e) {
      console.error('Error cargando backlinks:', e);
      setError(e?.response?.data?.detail || 'Error cargando backlinks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mes, filtroEstado, filtroPlataforma, incluirInactivos]);

  const [ordenSugerencias, setOrdenSugerencias] = useState('clicks_anuales');
  const [limitSugerencias, setLimitSugerencias] = useState(10);

  const cargarSugerencias = async (orden = ordenSugerencias, limit = limitSugerencias) => {
    setLoadingSug(true);
    try {
      const res = await marketingWebAPI.sugerenciasBacklinks({
        limit, ordenar_por: orden, min_impresiones: 30,
      });
      setSugerencias(res.data);
    } catch (e) {
      console.error('Error cargando sugerencias:', e);
      setSugerencias(null);
    } finally {
      setLoadingSug(false);
    }
  };

  useEffect(() => {
    cargarSugerencias(ordenSugerencias, limitSugerencias);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordenSugerencias, limitSugerencias]);

  const cargarCampanas = async () => {
    setLoadingCampanas(true);
    try {
      const res = await marketingWebAPI.listarCampanasBacklinks();
      setCampanas(res.data);
    } catch (e) {
      console.error('Error cargando campañas:', e);
      setCampanas(null);
    } finally {
      setLoadingCampanas(false);
    }
  };

  useEffect(() => {
    cargarCampanas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]); // refresca cuando cambia la lista de backlinks

  const onCrearDesdeSugerencia = (s) => {
    setEditing(null);
    setPrefillSugerencia({
      anchor_text: s.anchor_text_sugerido,
      tipo_anchor: s.tipo_anchor_sugerido,
      target_url: s.target_url_sugerida,
      kw_objetivo: s.kw,
      plataforma: s.plataforma_sugerida === 'esperar al mes siguiente' ? 'linkatomic' : s.plataforma_sugerida,
      precio_total_eur: s.coste_aproximado_eur || '',
      notas_articulo: `Sugerido por GSC: KW pos ${s.posicion_media} con ${s.impresiones_90d} imp/90d.\nFragmento sugerido:\n\n${s.fragmento_sugerido.replace('TARGET_URL', s.target_url_sugerida)}`,
    });
    setFormOpen(true);
  };

  const onVerHistorialGsc = async (bl) => {
    setHistorialGsc({ loading: true, backlink: bl });
    try {
      const res = await marketingWebAPI.historialGscBacklink(bl.id);
      setHistorialGsc({ loading: false, backlink: bl, data: res.data });
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Error cargando historial GSC';
      setHistorialGsc({ loading: false, backlink: bl, error: detail });
    }
  };

  const itemsFiltrados = useMemo(() => {
    if (!busqueda) return items;
    const q = busqueda.toLowerCase();
    return items.filter((b) =>
      (b.dominio || '').toLowerCase().includes(q) ||
      (b.anchor_text || '').toLowerCase().includes(q) ||
      (b.kw_objetivo || '').toLowerCase().includes(q),
    );
  }, [items, busqueda]);

  const onCrear = () => { setEditing(null); setFormOpen(true); };
  const onEditar = (bl) => { setEditing(bl); setFormOpen(true); };

  const onEliminar = async (bl) => {
    if (!window.confirm(`¿Eliminar el backlink de ${bl.dominio}? (soft delete, podrás recuperarlo con "Incluir inactivos")`)) return;
    try {
      await marketingWebAPI.eliminarBacklink(bl.id, false);
      mostrarToast('ok', 'Backlink eliminado (soft delete)');
      cargar();
    } catch (e) {
      mostrarToast('error', e?.response?.data?.detail || 'Error eliminando');
    }
  };

  const onVerificar = async (bl) => {
    setVerificando(bl.id);
    try {
      const res = await marketingWebAPI.verificarBacklink(bl.id);
      mostrarToast('ok', `Verificación OK: status ${res.data?.verificacion?.status_code || '?'}`);
      cargar();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 503) {
        mostrarToast('warn', 'Verificación HTTP aún no implementada (Fase 2). Solo registro manual de momento.');
      } else {
        mostrarToast('error', e?.response?.data?.detail || 'Error verificando');
      }
    } finally {
      setVerificando(null);
    }
  };

  const onVerificarTodos = async () => {
    setVerifAllRunning(true);
    try {
      await marketingWebAPI.verificarTodosBacklinks();
      mostrarToast('ok', 'Verificación masiva completada');
      cargar();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 503) {
        mostrarToast('warn', 'Verificación masiva aún no implementada (Fase 2).');
      } else {
        mostrarToast('error', e?.response?.data?.detail || 'Error verificando');
      }
    } finally {
      setVerifAllRunning(false);
    }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-duetto-primary flex items-center gap-2">
            <Link2 className="w-6 h-6" />
            Backlinks
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Enlaces SEO comprados (Linkatomic, Publisuites, …): compra, verificación y cruce con Search Console.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <button onClick={() => setPlanMensualOpen(true)}
            className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Plan del mes
          </button>
          <button onClick={onVerificarTodos} disabled={verifAllRunning}
            className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${verifAllRunning ? 'animate-spin' : ''}`} />
            Verificar todos
          </button>
          <a href={`/api${marketingWebAPI.exportarBacklinksCsvUrl(mes)}`} target="_blank" rel="noopener noreferrer"
            className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 flex items-center gap-2">
            <Download className="w-4 h-4" />
            CSV
          </a>
          <button onClick={() => setImportOpen(true)}
            className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Importar
          </button>
          <button onClick={onCrear}
            className="px-3 py-2 text-sm bg-duetto-primary text-white rounded-md hover:opacity-90 flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Nuevo backlink
          </button>
        </div>
      </div>

      {/* KPIs */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <KpiCard
            label="Presupuesto"
            value={`${fmtEur(kpis.gastado_eur)} / ${fmtEur(kpis.presupuesto_eur)}`}
            icon={Euro}
            hint={`Restante: ${fmtEur(kpis.restante_eur)}`}
            tone={kpis.restante_eur < 0 ? 'warn' : 'neutral'}
          />
          <KpiCard
            label="Backlinks"
            value={kpis.backlinks_totales}
            icon={Hash}
            hint={`${kpis.backlinks_vivos} vivos · ${kpis.backlinks_planificados} pdte.`}
          />
          <KpiCard
            label="DR medio"
            value={kpis.dr_medio ?? '—'}
            icon={Award}
          />
          <KpiCard
            label="Dofollow %"
            value={fmtPct(kpis.dofollow_pct)}
            icon={CheckCircle2}
            tone={kpis.dofollow_pct === 1 ? 'ok' : 'neutral'}
          />
          <KpiCard
            label="Exact match"
            value={fmtPct(kpis.pct_exact_match)}
            icon={AlertTriangle}
            tone={kpis.alerta_sobreoptimizacion ? 'warn' : 'neutral'}
            hint={kpis.alerta_sobreoptimizacion ? '⚠ >30 %, riesgo Google' : 'OK, perfil natural'}
          />
        </div>
      )}

      {/* Alertas resumen estado */}
      {kpis && (kpis.backlinks_caidos > 0 || kpis.backlinks_alterados > 0 || kpis.backlinks_bloqueados > 0) && (
        <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
          {kpis.backlinks_caidos > 0 && (
            <AlertBox tipo="error" icon={XCircle} texto={`${kpis.backlinks_caidos} enlace(s) caído(s) — reclama a la plataforma`} />
          )}
          {kpis.backlinks_alterados > 0 && (
            <AlertBox tipo="warn" icon={AlertTriangle} texto={`${kpis.backlinks_alterados} alterado(s) — anchor/rel cambió`} />
          )}
          {kpis.backlinks_bloqueados > 0 && (
            <AlertBox tipo="info" icon={ShieldQuestion} texto={`${kpis.backlinks_bloqueados} con anti-bot 403 — revisión manual`} />
          )}
        </div>
      )}

      {/* Panel de canibalización SEO */}
      {kpis?.canibalizacion && (
        (kpis.canibalizacion.overkill?.length > 0 || kpis.canibalizacion.conflictos?.length > 0) && (
          <div className="mb-4 bg-amber-50 border border-amber-300 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-amber-700" />
              <h3 className="font-semibold text-amber-900 text-sm">Alertas SEO de canibalización</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              {kpis.canibalizacion.conflictos?.length > 0 && (
                <div className="bg-white border border-red-200 rounded-md p-2.5">
                  <div className="font-semibold text-red-700 mb-1">
                    ⚠ {kpis.canibalizacion.conflictos.length} conflicto(s) de KW
                  </div>
                  <div className="text-[11px] text-gray-600 mb-1.5">
                    Una misma KW apunta a varias URLs distintas. Google no sabe cuál es la página dueña.
                  </div>
                  <ul className="space-y-1 text-[11px]">
                    {kpis.canibalizacion.conflictos.slice(0, 3).map((c, i) => (
                      <li key={i}>
                        <strong>"{c.kw}"</strong> ({c.n_backlinks} backlinks) →{' '}
                        {c.targets.map((t, j) => (
                          <span key={j} className="font-mono text-[10px]">
                            {t.replace('https://duettocatering.com', '') || '/'}
                            {j < c.targets.length - 1 && ', '}
                          </span>
                        ))}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {kpis.canibalizacion.overkill?.length > 0 && (
                <div className="bg-white border border-amber-200 rounded-md p-2.5">
                  <div className="font-semibold text-amber-700 mb-1">
                    ⚠ {kpis.canibalizacion.overkill.length} URL(s) sobre-optimizadas
                  </div>
                  <div className="text-[11px] text-gray-600 mb-1.5">
                    3+ backlinks apuntan a la misma URL. Considera diversificar o pausar.
                  </div>
                  <ul className="space-y-1 text-[11px]">
                    {kpis.canibalizacion.overkill.slice(0, 3).map((o, i) => (
                      <li key={i}>
                        <span className="font-mono text-[10px]">
                          {o.target_url.replace('https://duettocatering.com', '') || '/'}
                        </span>
                        : <strong>{o.n_backlinks} enlaces</strong> · {o.kws.length} KW(s)
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )
      )}

      {/* Filtros */}
      <div className="bg-white border rounded-lg p-3 mb-4 flex flex-wrap gap-3 items-center">
        <select value={mes} onChange={(e) => setMes(e.target.value)}
          className="px-3 py-2 text-sm border rounded-md">
          {rellenaMesOpciones().map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}
          className="px-3 py-2 text-sm border rounded-md">
          <option value="">Todos los estados</option>
          {ESTADOS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={filtroPlataforma} onChange={(e) => setFiltroPlataforma(e.target.value)}
          className="px-3 py-2 text-sm border rounded-md">
          <option value="">Todas las plataformas</option>
          {PLATAFORMAS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <label className="inline-flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={incluirInactivos} onChange={(e) => setIncluirInactivos(e.target.checked)} />
          Incluir inactivos
        </label>
        <div className="relative ml-auto">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
          <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar dominio, anchor o KW…"
            className="pl-9 pr-3 py-2 text-sm border rounded-md w-64" />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4 text-sm text-red-800">{error}</div>
      )}

      {/* Tabla */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Dominio</TableHead>
              <TableHead>Anchor</TableHead>
              <TableHead>Target</TableHead>
              <TableHead className="text-right">DR</TableHead>
              <TableHead>Plataforma</TableHead>
              <TableHead className="text-right">€</TableHead>
              <TableHead>KW objetivo</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={9} className="text-center text-gray-400 py-6">Cargando…</TableCell></TableRow>
            )}
            {!loading && itemsFiltrados.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-gray-400 py-10">
                Sin backlinks en este filtro. Pulsa <strong>Nuevo backlink</strong> para registrar el primero.
              </TableCell></TableRow>
            )}
            {!loading && itemsFiltrados.map((b) => (
              <TableRow key={b.id} className={!b.activo ? 'opacity-60' : ''}>
                <TableCell className="text-sm">
                  <div className="flex items-center gap-1">
                    <a href={b.url_post} target="_blank" rel="noopener noreferrer"
                      className="hover:underline text-gray-900" title={b.url_post}>
                      {b.dominio}
                    </a>
                    <ExternalLink className="w-3 h-3 text-gray-400 shrink-0" />
                  </div>
                  {b.fecha_compra && <div className="text-[10px] text-gray-400 mt-0.5">{b.fecha_compra}</div>}
                </TableCell>
                <TableCell className="text-sm">
                  <span className="font-medium">"{b.anchor_text}"</span>
                  <div className="text-[10px] text-gray-400 mt-0.5">
                    {TIPOS_ANCHOR.find((t) => t.value === b.tipo_anchor)?.label}
                    {!b.dofollow && <span className="ml-1 text-amber-600">· nofollow</span>}
                  </div>
                </TableCell>
                <TableCell className="text-xs font-mono text-gray-600">
                  {(b.target_url || '').replace('https://duettocatering.com', '') || '/'}
                </TableCell>
                <TableCell className="text-right text-sm font-medium">{b.dr_ahrefs ?? '—'}</TableCell>
                <TableCell className="text-xs">
                  {PLATAFORMAS.find((p) => p.value === b.plataforma)?.label || b.plataforma}
                </TableCell>
                <TableCell className="text-right text-sm">{fmtEur(b.precio_eur)}</TableCell>
                <TableCell className="text-xs">{b.kw_objetivo || '—'}</TableCell>
                <TableCell><EstadoBadge value={b.estado} /></TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <IconBtn icon={Eye} title="Detalle" onClick={() => setDetalle(b)} />
                    <IconBtn icon={RefreshCw} title="Verificar ahora" onClick={() => onVerificar(b)}
                      spinning={verificando === b.id} />
                    <IconBtn icon={Pencil} title="Editar" onClick={() => onEditar(b)} />
                    <IconBtn icon={Trash2} title="Eliminar" onClick={() => onEliminar(b)} danger />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Campañas SEO (agrupadas por kw_objetivo) */}
      {campanas?.campanas?.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-5 h-5 text-indigo-600" />
            <h2 className="font-semibold text-gray-800">Campañas SEO activas</h2>
            <span className="text-xs text-gray-500 ml-2">
              {campanas.total_campanas} campañas · {fmtEur(campanas.total_inversion_eur)} invertidos
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {campanas.campanas.map((c) => (
              <CampanaCard key={c.kw_objetivo} c={c} />
            ))}
          </div>
        </div>
      )}

      {/* Donut distribución anchors + KWs priorizadas del plan */}
      {kpis && kpis.backlinks_totales > 0 && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white border rounded-lg p-4 lg:col-span-1">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Distribución de anchors ({mes})</h3>
            <DonutAnchors distribucion={kpis.distribucion_anchors} alerta={kpis.alerta_sobreoptimizacion} />
          </div>
          <PlanResumen mes={mes} mostrarToast={mostrarToast} onEditar={() => setPlanMensualOpen(true)} />
        </div>
      )}

      {/* Sugerencias GSC + Keyword Planner */}
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Sparkles className="w-5 h-5 text-purple-600" />
          <h2 className="font-semibold text-gray-800">Sugerencias (GSC + Keyword Planner)</h2>
          <select value={ordenSugerencias} onChange={(e) => setOrdenSugerencias(e.target.value)}
            className="ml-4 px-2 py-1 text-xs border rounded-md">
            <option value="clicks_anuales">Más clicks/año (descubrir)</option>
            <option value="roi_ano_1">Mayor ROI año 1 (rentable rápido)</option>
            <option value="roi_vida_util">Mayor ROI vida útil (largo plazo)</option>
            <option value="facilidad">Más fácil (pocos enlaces necesarios)</option>
            <option value="volumen">Mayor volumen de búsquedas</option>
          </select>
          <select value={limitSugerencias} onChange={(e) => setLimitSugerencias(Number(e.target.value))}
            className="px-2 py-1 text-xs border rounded-md">
            <option value={5}>Top 5</option>
            <option value={10}>Top 10</option>
            <option value={20}>Top 20</option>
            <option value={50}>Top 50</option>
          </select>
          <button onClick={() => cargarSugerencias()} className="ml-auto text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
            title="Recargar desde cache (si tiene <6h, instantáneo)">
            <RefreshCw className={`w-3 h-3 ${loadingSug ? 'animate-spin' : ''}`} />
            Recargar
          </button>
          <button
            onClick={async () => {
              try {
                await marketingWebAPI.invalidarCacheSugerencias();
                mostrarToast('ok', 'Cache invalidado, recalculando…');
                await cargarSugerencias();
              } catch (e) {
                mostrarToast('error', 'Error invalidando cache');
              }
            }}
            className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1"
            title="Borra el cache y recalcula desde GSC + Keyword Planner (10-30s)">
            <Sparkles className="w-3 h-3" />
            Forzar recálculo
          </button>
        </div>
        {sugerencias?.from_cache && (
          <div className="mb-2 text-[11px] text-gray-500 bg-gray-50 border rounded px-2 py-1 inline-block">
            ✓ Datos cacheados ({sugerencias.cache_edad_minutos} min). Cache TTL: 6h. Pulsa "Forzar recálculo" si quieres refrescar.
          </div>
        )}
        {loadingSug && (
          <div className="bg-white border rounded-lg p-4 text-sm text-gray-500">Cargando sugerencias…</div>
        )}
        {!loadingSug && (!sugerencias || sugerencias.candidatas?.length === 0) && (
          <div className="bg-gray-50 border rounded-lg p-4 text-sm text-gray-500">
            No hay KWs candidatas (pos 4-15 con imp {'>'}50 que no estén ya cubiertas por un backlink activo).
            <br />
            <span className="text-xs">Analizadas {sugerencias?.total_kws_analizadas || 0} KWs de los últimos {sugerencias?.rango_analizado_dias || 90} días.</span>
          </div>
        )}
        {!loadingSug && sugerencias && sugerencias.candidatas?.length > 0 && (
          <>
            {/* Resumen agregado: si trabajaras TODAS las candidatas */}
            {sugerencias.totales && (
              <div className="mb-3 bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-indigo-700" />
                  <h3 className="font-semibold text-indigo-900 text-sm">
                    Potencial total si trabajas las {sugerencias.totales.n_candidatas_total} KWs sugeridas
                  </h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                  <div className="bg-white/70 rounded p-2">
                    <div className="text-[10px] uppercase text-gray-600 font-semibold">Backlinks</div>
                    <div className="text-lg font-bold text-indigo-900">{sugerencias.totales.backlinks_total_necesarios}</div>
                    <div className="text-[10px] text-gray-500">{fmtEur(sugerencias.totales.inversion_total)} inversión</div>
                  </div>
                  <div className="bg-white/70 rounded p-2">
                    <div className="text-[10px] uppercase text-gray-600 font-semibold">Pptos conf/año</div>
                    <div className="text-lg font-bold text-indigo-900">{sugerencias.totales.pptos_confirmados_total}</div>
                    <div className="text-[10px] text-gray-500">{fmtNum(sugerencias.totales.clicks_anuales_total)} clicks/año</div>
                  </div>
                  <div className="bg-white/70 rounded p-2">
                    <div className="text-[10px] uppercase text-gray-600 font-semibold">Ingresos/año</div>
                    <div className="text-lg font-bold text-emerald-700">{fmtEur(sugerencias.totales.ingresos_total_anuales)}</div>
                    <div className="text-[10px] text-gray-500">{fmtEur(sugerencias.totales.ingresos_total_vida_util)} a {sugerencias.supuestos?.vida_util_backlink_anios}a</div>
                  </div>
                  <div className="bg-white/70 rounded p-2">
                    <div className="text-[10px] uppercase text-gray-600 font-semibold">Beneficio año 1</div>
                    <div className={`text-lg font-bold ${sugerencias.totales.beneficio_total_ano_1 >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      {sugerencias.totales.beneficio_total_ano_1 >= 0 ? '+' : ''}{fmtEur(sugerencias.totales.beneficio_total_ano_1)}
                    </div>
                    <div className="text-[10px] text-gray-500">a {sugerencias.supuestos?.vida_util_backlink_anios}a: {fmtEur(sugerencias.totales.beneficio_total_vida_util)}</div>
                  </div>
                  <div className="bg-white/70 rounded p-2">
                    <div className="text-[10px] uppercase text-gray-600 font-semibold">Ahorro vs Ads</div>
                    <div className="text-lg font-bold text-blue-700">{fmtEur(sugerencias.totales.ahorro_ads_total_anual)}/año</div>
                    <div className="text-[10px] text-gray-500">{fmtEur(sugerencias.totales.ahorro_ads_total_vida_util)} a {sugerencias.supuestos?.vida_util_backlink_anios}a</div>
                  </div>
                </div>
                <div className="text-[10px] text-gray-500 mt-2 italic">
                  Estimación basada en ticket real {fmtEur(sugerencias.supuestos?.ticket_medio_eur)}, click→lead {sugerencias.supuestos?.click_a_lead_pct}%
                  {sugerencias.supuestos?.click_a_lead_ads_real_pct && ' (real Ads)'}, lead→cliente {sugerencias.supuestos?.lead_a_cliente_pct}%.
                </div>
              </div>
            )}
            <div className="text-xs text-gray-500 mb-2">
              {sugerencias.candidatas.length} candidatas visibles · presupuesto restante del mes: <strong>{fmtEur(sugerencias.restante_mes)}</strong>
              {sugerencias.restante_mes <= 0 && <span className="ml-2 text-amber-700">(esperar al mes siguiente)</span>}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {sugerencias.candidatas.map((s) => (
                <SugerenciaCard
                  key={s.kw}
                  s={s}
                  supuestos={sugerencias.supuestos}
                  onCrear={() => onCrearDesdeSugerencia(s)}
                  mostrarToast={mostrarToast}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <BacklinkFormDialog
        open={formOpen}
        initial={editing}
        prefill={prefillSugerencia}
        onClose={() => { setFormOpen(false); setPrefillSugerencia(null); }}
        onSaved={() => { mostrarToast('ok', editing ? 'Backlink actualizado' : 'Backlink creado'); cargar(); cargarSugerencias(); }}
      />
      <ImportarDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => { cargar(); cargarSugerencias(); }}
      />
      <DetalleDialog
        backlink={detalle}
        onClose={() => setDetalle(null)}
        onVerHistorial={() => { setDetalle(null); onVerHistorialGsc(detalle); }}
      />
      <HistorialGscDialog estado={historialGsc} onClose={() => setHistorialGsc(null)} />
      <PlanMensualDialog
        open={planMensualOpen}
        mes={mes}
        onClose={() => setPlanMensualOpen(false)}
        onSaved={() => { mostrarToast('ok', 'Plan del mes guardado'); cargar(); }}
      />

      {toast && (
        <div className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg text-sm font-medium z-50 ${
          toast.tipo === 'ok' ? 'bg-emerald-600 text-white' :
          toast.tipo === 'warn' ? 'bg-amber-500 text-white' :
          'bg-red-600 text-white'
        }`}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

function IconBtn({ icon: Icon, title, onClick, danger, spinning }) {
  return (
    <button onClick={onClick} title={title}
      className={`p-1.5 rounded hover:bg-gray-100 ${danger ? 'text-red-600' : 'text-gray-600'}`}>
      <Icon className={`w-4 h-4 ${spinning ? 'animate-spin' : ''}`} />
    </button>
  );
}

function AlertBox({ tipo, icon: Icon, texto }) {
  const cls = {
    error: 'bg-red-50 border-red-200 text-red-800',
    warn: 'bg-amber-50 border-amber-200 text-amber-800',
    info: 'bg-purple-50 border-purple-200 text-purple-800',
  }[tipo];
  return (
    <div className={`border rounded-md p-2.5 flex items-center gap-2 ${cls}`}>
      <Icon className="w-4 h-4 shrink-0" />
      <span>{texto}</span>
    </div>
  );
}

function SugerenciaCard({ s, onCrear, mostrarToast, supuestos }) {
  const fragmento = (s.fragmento_sugerido || '').replace('TARGET_URL', s.target_url_sugerida);
  const copiarFragmento = async () => {
    try {
      await navigator.clipboard.writeText(fragmento);
      mostrarToast?.('ok', 'Fragmento copiado al portapapeles');
    } catch (e) {
      mostrarToast?.('error', 'No se pudo copiar (revisa permisos del navegador)');
    }
  };

  const tipoBadge = {
    exact_match: 'bg-red-100 text-red-700',
    partial: 'bg-orange-100 text-orange-700',
    brand: 'bg-blue-100 text-blue-700',
  }[s.tipo_anchor_sugerido] || 'bg-gray-100 text-gray-700';

  // Prioridad (rentabilidad): label distinto para no confundir con dificultad
  const prioridadCfg = {
    alta: { label: s.prioridad_label || 'RENTABLE', cls: 'bg-emerald-600 text-white' },
    media: { label: s.prioridad_label || 'ACEPTABLE', cls: 'bg-amber-500 text-white' },
    baja: { label: s.prioridad_label || 'POBRE', cls: 'bg-gray-400 text-white' },
  }[s.prioridad] || { label: 'ACEPTABLE', cls: 'bg-gray-300 text-gray-700' };

  // Dificultad (esfuerzo): label con número EXPLÍCITO de enlaces para no
  // solapar con prioridad. "8 ENLACES" ≠ "ALTA prioridad".
  const dificultadCfg = {
    facil: { cls: 'bg-green-100 text-green-700 border-green-300' },
    media: { cls: 'bg-amber-100 text-amber-700 border-amber-300' },
    alta: { cls: 'bg-orange-100 text-orange-700 border-orange-300' },
    muy_alta: { cls: 'bg-red-100 text-red-700 border-red-300' },
  }[s.dificultad] || { cls: 'bg-gray-100 text-gray-600 border-gray-300' };
  const dificultadLabel = s.dificultad_label || '—';

  const fuenteVolumenLabel = {
    keyword_planner: '✓ Keyword Planner',
    heuristica_gsc: '~ estimado GSC',
    sin_datos: 'sin datos',
  }[s.fuente_volumen] || s.fuente_volumen;

  const roiCls = (s.roi_vida_util ?? 0) >= 5
    ? 'border-emerald-300 bg-emerald-50'
    : (s.roi_vida_util ?? 0) >= 2
      ? 'border-amber-300 bg-amber-50'
      : 'border-red-300 bg-red-50';

  const posDisplay = s.posicion_media != null ? s.posicion_media : 'no rankeas';

  return (
    <div className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${prioridadCfg.cls}`}>
              {prioridadCfg.label}
            </span>
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${dificultadCfg.cls}`}>
              {dificultadLabel}
            </span>
            {s.origen_kw === 'manual' && (
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-purple-100 text-purple-700 border border-purple-300">
                MANUAL
              </span>
            )}
            <span className="font-medium text-sm text-gray-900 truncate">{s.kw}</span>
          </div>
          <div className="text-[11px] text-gray-500">
            pos <strong>{posDisplay}</strong> · {fmtNum(s.volumen_mensual_estimado)} búsq/mes ({fuenteVolumenLabel})
            {s.competencia_label && <> · comp. <strong>{s.competencia_label}</strong></>}
            {s.cpc_high_eur && <> · CPC ~{fmtEur(s.cpc_high_eur)}</>}
          </div>
        </div>
        <button onClick={onCrear}
          className="px-2.5 py-1 text-xs bg-duetto-primary text-white rounded hover:opacity-90 shrink-0 flex items-center gap-1">
          <Plus className="w-3 h-3" />
          Crear
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs mt-3">
        <div>
          <div className="text-[10px] uppercase text-gray-500 font-semibold">Anchor sugerido</div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${tipoBadge}`}>
              {s.tipo_anchor_sugerido}
            </span>
            <span className="truncate" title={s.anchor_text_sugerido}>"{s.anchor_text_sugerido}"</span>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-gray-500 font-semibold">Target sugerida</div>
          <div className="font-mono truncate text-gray-700" title={s.target_url_sugerida}>
            {s.target_url_sugerida.replace('https://duettocatering.com', '') || '/'}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-gray-500 font-semibold">Inversión total</div>
          <div>
            <strong>{s.backlinks_necesarios}</strong> backlinks ·{' '}
            <strong>{fmtEur(s.inversion_total_estimada_eur)}</strong>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-gray-500 font-semibold">Plataforma</div>
          <div>{PLATAFORMAS.find((p) => p.value === s.plataforma_sugerida)?.label || s.plataforma_sugerida}</div>
        </div>
      </div>

      {/* Funnel previsto: clicks → leads → presupuestos → ingresos */}
      <div className="mt-3 border border-purple-200 bg-purple-50/50 rounded-md p-2.5 text-xs">
        <div className="text-[10px] uppercase font-semibold text-purple-700 mb-1.5">
          Funnel previsto al año (si posicionas top 5)
        </div>
        <div className="flex items-center justify-between gap-1 text-[11px]">
          <FunnelStep label="Clicks/año" valor={fmtNum(s.clicks_anuales_estimados)} />
          <FunnelArrow />
          <FunnelStep label="Leads/año" valor={fmtNum(s.leads_anuales_estimados)} sublabel={`${(supuestos?.click_a_lead_pct ?? 2)}%`} />
          <FunnelArrow />
          <FunnelStep
            label="Pptos confirmados"
            valor={fmtNum(s.presupuestos_confirmados_anuales ?? s.clientes_anuales_estimados)}
            sublabel={`${(supuestos?.lead_a_cliente_pct ?? 15)}%`}
            destacado
          />
          <FunnelArrow />
          <FunnelStep label="Ingresos/año" valor={fmtEur(s.ingresos_anuales_estimados)} />
        </div>
      </div>

      {/* Bloque Beneficio estimado (€ absolutos) */}
      <div className={`mt-2 border rounded-md p-2.5 text-xs ${roiCls}`}>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] uppercase font-semibold text-gray-600">Beneficio estimado</span>
          {s.tiempo_recuperacion_meses != null && (
            <span className="text-[10px] text-gray-600">
              Recuperas en <strong>{s.tiempo_recuperacion_meses} meses</strong>
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
          <div>Ingresos/año: <strong>{fmtEur(s.ingresos_anuales_estimados)}</strong></div>
          <div>Inversión: <strong>{fmtEur(s.inversion_total_estimada_eur)}</strong></div>
          <div className="col-span-2 pt-1 border-t border-gray-200/60 mt-1">
            <span className="text-gray-600">Beneficio año 1:</span>{' '}
            <strong className={(s.beneficio_ano_1_eur ?? 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}>
              {(s.beneficio_ano_1_eur ?? 0) >= 0 ? '+' : ''}{fmtEur(s.beneficio_ano_1_eur)}
            </strong>
            {' · '}
            <span className="text-gray-600">a {supuestos?.vida_util_backlink_anios ?? 2}a:</span>{' '}
            <strong className={(s.beneficio_vida_util_eur ?? 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}>
              {(s.beneficio_vida_util_eur ?? 0) >= 0 ? '+' : ''}{fmtEur(s.beneficio_vida_util_eur)}
            </strong>
            <span className="text-gray-400 ml-1">({s.roi_ano_1}x · {s.roi_vida_util}x)</span>
          </div>
        </div>
        {supuestos && (
          <div className="text-[10px] text-gray-500 mt-1.5 italic space-y-0.5">
            <div>
              Supuestos: ticket <strong>{fmtEur(supuestos.ticket_medio_eur)}</strong>
              {supuestos.ticket_medio_n_presupuestos != null && (
                <span title={supuestos.ticket_medio_fuente}> ({supuestos.ticket_medio_n_presupuestos} ppto.)</span>
              )}
              {' · '}CTR top 5 {supuestos.ctr_top5_estimado_pct}% ·
              click→lead <strong>{supuestos.click_a_lead_pct}%</strong>
              {supuestos.click_a_lead_ads_real_pct != null && (
                <span className="text-emerald-700" title={supuestos.click_a_lead_fuente}>
                  {' '}✓ real Ads {supuestos.click_a_lead_ads_real_pct}%
                </span>
              )}
              {' · '}lead→cliente {supuestos.lead_a_cliente_pct}%
            </div>
            {supuestos.click_a_lead_clicks_ads_muestra && (
              <div className="text-[9px] text-gray-400">
                Tasa click→lead calculada con {fmtNum(supuestos.click_a_lead_clicks_ads_muestra)} clicks /
                {' '}{supuestos.click_a_lead_conv_ads_muestra} convs reales de Google Ads (90d), CPL {fmtEur(supuestos.cpl_real_ads_eur)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Ahorro vs Google Ads — caja propia separada, siempre visible */}
      <div className="mt-2 border border-blue-200 bg-blue-50 rounded-md p-2.5 text-xs">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] uppercase font-semibold text-blue-700">💡 Ahorro vs Google Ads</span>
          {s.cpc_high_eur != null ? (
            <span className="text-[10px] text-gray-500">CPC ~{fmtEur(s.cpc_high_eur)}</span>
          ) : (
            <span className="text-[10px] text-gray-400">CPC no disponible</span>
          )}
        </div>
        {s.ahorro_anual_vs_ads_eur != null ? (
          <>
            <div className="text-[11px] text-gray-700">
              Conseguir <strong>{fmtNum(s.clicks_anuales_estimados)} clicks/año</strong> en Google Ads costaría:
            </div>
            <div className="grid grid-cols-2 gap-x-3 mt-1 text-[11px]">
              <div>
                <span className="text-gray-600">Año 1:</span>{' '}
                <strong className="text-blue-700 text-sm">{fmtEur(s.ahorro_anual_vs_ads_eur)}</strong>
              </div>
              <div>
                <span className="text-gray-600">A {supuestos?.vida_util_backlink_anios ?? 2}a:</span>{' '}
                <strong className="text-blue-700 text-sm">{fmtEur(s.ahorro_vida_util_vs_ads_eur)}</strong>
              </div>
            </div>
            <div className="text-[10px] text-gray-500 mt-1 italic">
              Es lo que ahorras al posicionar orgánicamente: ese dinero NO lo pagas a Google Ads.
            </div>
          </>
        ) : (
          <div className="text-[11px] text-gray-500 italic">
            Google Keyword Planner no devolvió CPC para esta KW. Se usaría
            estimación heurística si hubiera más datos.
          </div>
        )}
      </div>

      <details className="mt-3">
        <summary className="text-xs text-purple-700 cursor-pointer hover:text-purple-900">
          Fragmento sugerido para el artículo ({fragmento.split(' ').length} palabras)
        </summary>
        <div className="mt-2 text-xs bg-purple-50 border border-purple-200 rounded p-2.5 whitespace-pre-wrap">
          {fragmento}
        </div>
        <button onClick={copiarFragmento}
          className="mt-2 text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1">
          <Download className="w-3 h-3" />
          Copiar al portapapeles
        </button>
      </details>
    </div>
  );
}

function CampanaCard({ c }) {
  // Color de la barra de progreso según completitud
  const barColor = c.progreso_pct >= 100
    ? 'bg-emerald-500'
    : c.progreso_pct >= 50
      ? 'bg-amber-500'
      : 'bg-indigo-500';

  // Conteo de estados con colores
  const estados = c.estados || {};
  const totalActivos = c.n_backlinks_actuales;

  return (
    <div className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-gray-900 truncate" title={c.kw_objetivo}>
            {c.kw_objetivo}
          </div>
          {c.pos_gsc_inicio_min != null && (
            <div className="text-[10px] text-gray-500">
              pos inicial GSC: {c.pos_gsc_inicio_min}
            </div>
          )}
        </div>
        {c.alerta_sobreoptimizacion && (
          <div title="Más del 30% son exact_match — riesgo de penalización">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
          </div>
        )}
      </div>

      {/* Barra de progreso */}
      <div className="mb-2">
        <div className="flex items-center justify-between text-[11px] mb-1">
          <span className="text-gray-600">
            <strong>{c.n_backlinks_actuales}</strong> / {c.n_backlinks_estimados} backlinks
          </span>
          <span className="font-semibold text-gray-700">{c.progreso_pct}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div className={`${barColor} h-1.5 rounded-full transition-all`} style={{ width: `${Math.min(c.progreso_pct, 100)}%` }} />
        </div>
      </div>

      {/* Inversión */}
      <div className="grid grid-cols-2 gap-2 text-[11px] mt-2">
        <div>
          <div className="text-[10px] uppercase text-gray-500 font-semibold">Invertido</div>
          <div className="font-semibold">{fmtEur(c.inversion_actual_eur)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-gray-500 font-semibold">Restante estimado</div>
          <div className="font-semibold text-gray-700">{fmtEur(c.restante_eur)}</div>
        </div>
      </div>

      {/* Distribución de estados (chips pequeños) */}
      {Object.keys(estados).length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {Object.entries(estados).map(([estado, n]) => (
            <span key={estado} className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
              {n} {estado}
            </span>
          ))}
        </div>
      )}

      {/* Distribución anchors (compacta) */}
      {Object.keys(c.distribucion_anchors || {}).length > 0 && (
        <div className="text-[10px] text-gray-500 mt-2">
          Anchors:{' '}
          {Object.entries(c.distribucion_anchors).map(([t, n], i, arr) => (
            <span key={t}>
              {n} {t}{i < arr.length - 1 ? ' · ' : ''}
            </span>
          ))}
          {c.pct_exact_match > 0.3 && (
            <span className="text-amber-700 font-semibold ml-1">
              ({(c.pct_exact_match * 100).toFixed(0)}% exact ⚠)
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function FunnelStep({ label, valor, sublabel, destacado }) {
  return (
    <div className={`flex-1 text-center px-1 ${destacado ? 'bg-white border border-purple-300 rounded shadow-sm py-1' : ''}`}>
      <div className={`font-bold ${destacado ? 'text-purple-700 text-sm' : 'text-gray-900'}`}>
        {valor}
      </div>
      <div className="text-[9px] uppercase text-gray-600 leading-tight">{label}</div>
      {sublabel && <div className="text-[9px] text-gray-400 leading-tight">({sublabel})</div>}
    </div>
  );
}

function FunnelArrow() {
  return <div className="text-purple-300 text-xs px-0.5">→</div>;
}

function HistorialGscDialog({ estado, onClose }) {
  if (!estado) return null;
  const { backlink, data, loading, error } = estado;

  return (
    <Dialog open={!!estado} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            Historial GSC del backlink
          </DialogTitle>
          <DialogDescription>
            Cómo se mueve la KW objetivo <strong>"{backlink?.kw_objetivo || ''}"</strong> antes
            y después de la compra del backlink ({backlink?.fecha_compra || '—'}).
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          {loading && <div className="text-sm text-gray-500 py-6 text-center">Cargando datos de Search Console…</div>}
          {error && <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">{error}</div>}
          {data && data.ok && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <PeriodoCard titulo="Pre-compra (30 días previos)" tone="neutral" snap={data.pre} />
                <PeriodoCard
                  titulo="Post-compra (desde fecha de compra)"
                  tone={data.mejora ? 'ok' : 'neutral'}
                  snap={data.post}
                />
              </div>
              <div className="bg-gray-50 border rounded-md p-3">
                <div className="text-xs uppercase tracking-wide font-semibold text-gray-500 mb-1">Delta</div>
                <div className="text-sm">
                  {data.delta_posicion == null ? (
                    <span className="text-gray-500">No hay datos suficientes en post-compra todavía.</span>
                  ) : (
                    <>
                      Posición: <strong className={data.mejora ? 'text-emerald-700' : 'text-amber-700'}>
                        {data.delta_posicion > 0 ? '+' : ''}{data.delta_posicion}
                      </strong>{' '}
                      {data.mejora ? '(mejora ↑)' : '(retroceso ↓)'}
                      {' · '}Δ impresiones: <strong>{data.delta_impresiones > 0 ? '+' : ''}{data.delta_impresiones}</strong>
                    </>
                  )}
                </div>
              </div>
              <div className="mt-3 text-[11px] text-gray-500">
                Nota: GSC tiene latencia de 2-3 días y el módulo agrega desde el top 20 de queries
                por día. KWs que dejan de aparecer en el top 20 no se cuentan.
              </div>
            </>
          )}
        </div>

        <DialogFooter className="mt-4">
          <button onClick={onClose} className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50">Cerrar</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DonutAnchors({ distribucion, alerta }) {
  // Colores por tipo de anchor consistentes con los badges del modal
  const COLORS = {
    exact_match: '#DC2626', // rojo (riesgo si >30%)
    partial: '#EA580C',     // naranja
    brand: '#2563EB',       // azul (seguro)
    url: '#6B7280',         // gris
    generico: '#10B981',    // verde (natural)
    imagen: '#A855F7',      // morado
  };
  const labels = Object.keys(distribucion || {}).filter((k) => distribucion[k] > 0);
  if (labels.length === 0) {
    return <div className="text-xs text-gray-400 text-center py-8">Sin datos para el mes</div>;
  }
  const data = {
    labels: labels.map((l) => TIPOS_ANCHOR.find((t) => t.value === l)?.label || l),
    datasets: [{
      data: labels.map((l) => distribucion[l]),
      backgroundColor: labels.map((l) => COLORS[l] || '#9CA3AF'),
      borderWidth: 2,
      borderColor: '#fff',
    }],
  };
  const total = labels.reduce((acc, l) => acc + distribucion[l], 0);
  return (
    <>
      <div className="relative h-48">
        <Doughnut
          data={data}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            cutout: '60%',
            plugins: {
              legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } },
              tooltip: {
                callbacks: {
                  label: (ctx) => {
                    const v = ctx.parsed;
                    const pct = total ? ((v / total) * 100).toFixed(0) : 0;
                    return `${ctx.label}: ${v} (${pct}%)`;
                  },
                },
              },
            },
          }}
        />
      </div>
      {alerta && (
        <div className="mt-2 text-[11px] bg-amber-50 border border-amber-200 rounded p-2 text-amber-800 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>
            Más del 30 % son <strong>exact_match</strong>: Google puede penalizar por
            sobre-optimización. Próximas compras prioriza <strong>partial</strong> o <strong>brand</strong>.
          </span>
        </div>
      )}
    </>
  );
}

function PlanResumen({ mes, mostrarToast, onEditar }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    marketingWebAPI.obtenerPlanBacklinks(mes)
      .then((res) => setPlan(res.data))
      .catch((e) => mostrarToast?.('error', e?.response?.data?.detail || 'Error cargando plan'))
      .finally(() => setLoading(false));
  }, [mes, mostrarToast]);

  return (
    <div className="bg-white border rounded-lg p-4 lg:col-span-2">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">Plan del mes ({mes})</h3>
        <button onClick={onEditar}
          className="text-xs text-duetto-primary hover:underline flex items-center gap-1">
          <Pencil className="w-3 h-3" />
          Editar
        </button>
      </div>
      {loading && <div className="text-xs text-gray-500">Cargando…</div>}
      {!loading && plan && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-[11px] uppercase tracking-wide font-semibold text-gray-500">Presupuesto</div>
            <div className="text-xl font-bold text-gray-900">{fmtEur(plan.presupuesto_eur)}</div>
            {plan._default && <div className="text-[10px] text-gray-400 mt-0.5">(default · no guardado)</div>}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide font-semibold text-gray-500">Notas</div>
            <div className="text-sm whitespace-pre-wrap">{plan.notas || <span className="text-gray-400">,</span>}</div>
          </div>
          <div className="md:col-span-2">
            <div className="text-[11px] uppercase tracking-wide font-semibold text-gray-500 mb-1">Objetivos</div>
            {plan.objetivos?.length > 0 ? (
              <ul className="list-disc list-inside text-sm space-y-0.5">
                {plan.objetivos.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            ) : (
              <span className="text-xs text-gray-400">Sin objetivos definidos</span>
            )}
          </div>
          <div className="md:col-span-2">
            <div className="text-[11px] uppercase tracking-wide font-semibold text-gray-500 mb-1">KWs priorizadas</div>
            {plan.kws_priorizadas?.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {plan.kws_priorizadas.map((k, i) => (
                  <span key={i} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">{k}</span>
                ))}
              </div>
            ) : (
              <span className="text-xs text-gray-400">Sin KWs priorizadas</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PlanMensualDialog({ open, mes, onClose, onSaved }) {
  const [form, setForm] = useState({
    presupuesto_eur: 300, objetivos: [], kws_priorizadas: [], notas: '',
    ticket_medio_eur: 3000, click_a_lead_pct: 2.0, lead_a_cliente_pct: 15.0, vida_util_backlink_anios: 2.0,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [nuevoObjetivo, setNuevoObjetivo] = useState('');
  const [nuevaKw, setNuevaKw] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    marketingWebAPI.obtenerPlanBacklinks(mes)
      .then((res) => {
        setForm({
          presupuesto_eur: res.data.presupuesto_eur ?? 300,
          objetivos: res.data.objetivos || [],
          kws_priorizadas: res.data.kws_priorizadas || [],
          notas: res.data.notas || '',
          ticket_medio_eur: res.data.ticket_medio_eur ?? 3000,
          click_a_lead_pct: res.data.click_a_lead_pct ?? 2.0,
          lead_a_cliente_pct: res.data.lead_a_cliente_pct ?? 15.0,
          vida_util_backlink_anios: res.data.vida_util_backlink_anios ?? 2.0,
        });
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Error cargando plan'))
      .finally(() => setLoading(false));
  }, [open, mes]);

  const addObjetivo = () => {
    const v = nuevoObjetivo.trim();
    if (!v) return;
    setForm((f) => ({ ...f, objetivos: [...f.objetivos, v] }));
    setNuevoObjetivo('');
  };
  const rmObjetivo = (i) => setForm((f) => ({ ...f, objetivos: f.objetivos.filter((_, idx) => idx !== i) }));
  const addKw = () => {
    const v = nuevaKw.trim().toLowerCase();
    if (!v || form.kws_priorizadas.includes(v)) return;
    setForm((f) => ({ ...f, kws_priorizadas: [...f.kws_priorizadas, v] }));
    setNuevaKw('');
  };
  const rmKw = (i) => setForm((f) => ({ ...f, kws_priorizadas: f.kws_priorizadas.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await marketingWebAPI.actualizarPlanBacklinks(mes, {
        presupuesto_eur: Number(form.presupuesto_eur) || 0,
        objetivos: form.objetivos,
        kws_priorizadas: form.kws_priorizadas,
        notas: form.notas,
        ticket_medio_eur: Number(form.ticket_medio_eur) || 3000,
        click_a_lead_pct: Number(form.click_a_lead_pct) || 2.0,
        lead_a_cliente_pct: Number(form.lead_a_cliente_pct) || 15.0,
        vida_util_backlink_anios: Number(form.vida_util_backlink_anios) || 2.0,
      });
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Error guardando plan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle>Plan del mes ({mes})</DialogTitle>
          <DialogDescription>
            Presupuesto y objetivos SEO para este mes. El presupuesto se compara con lo
            gastado en los backlinks del mes para mostrar el restante en KPIs.
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="py-6 text-center text-sm text-gray-500">Cargando…</div>}

        {!loading && (
          <form onSubmit={submit} className="space-y-4 mt-2">
            <Campo label="Presupuesto del mes (€)">
              <input type="number" step="1" min="0" value={form.presupuesto_eur}
                onChange={(e) => setForm((f) => ({ ...f, presupuesto_eur: e.target.value }))}
                className="w-full px-3 py-2 text-sm border rounded-md" />
            </Campo>

            <fieldset className="border rounded-md p-3 bg-emerald-50/30">
              <legend className="text-xs font-semibold text-gray-600 px-1">Supuestos para cálculo de ROI</legend>
              <div className="text-[10px] text-gray-500 mb-2 italic">
                💡 El ticket medio se calcula automáticamente desde tus presupuestos confirmados
                (mín. 50, año en curso + anterior si no llega). Solo lo cambies aquí si quieres
                forzar un valor distinto. Para usar el calculado real, déjalo en 3000.
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Campo label="Ticket medio (€) — override" hint="3000 = usa el real de tu BD">
                  <input type="number" step="50" min="0" value={form.ticket_medio_eur}
                    onChange={(e) => setForm((f) => ({ ...f, ticket_medio_eur: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border rounded-md" />
                </Campo>
                <Campo label="Click → Lead (%)" hint="De los clicks SEO, % que contactan">
                  <input type="number" step="0.1" min="0" max="100" value={form.click_a_lead_pct}
                    onChange={(e) => setForm((f) => ({ ...f, click_a_lead_pct: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border rounded-md" />
                </Campo>
                <Campo label="Lead → Cliente (%)" hint="% de leads que cierran">
                  <input type="number" step="0.1" min="0" max="100" value={form.lead_a_cliente_pct}
                    onChange={(e) => setForm((f) => ({ ...f, lead_a_cliente_pct: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border rounded-md" />
                </Campo>
                <Campo label="Vida útil (años)" hint="Cuánto durará el enlace activo">
                  <input type="number" step="0.5" min="0.5" max="10" value={form.vida_util_backlink_anios}
                    onChange={(e) => setForm((f) => ({ ...f, vida_util_backlink_anios: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border rounded-md" />
                </Campo>
              </div>
              <div className="text-[10px] text-gray-500 mt-2 italic">
                Estos valores se usan para calcular el ROI estimado de cada KW sugerida.
                Cambiarlos NO altera datos históricos, solo la próxima recomendación.
              </div>
            </fieldset>

            <div>
              <div className="text-xs font-medium text-gray-600 mb-1">Objetivos del mes</div>
              <div className="space-y-1.5 mb-2">
                {form.objetivos.map((o, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm bg-gray-50 rounded px-2 py-1">
                    <span className="flex-1">{o}</span>
                    <button type="button" onClick={() => rmObjetivo(i)}
                      className="text-gray-400 hover:text-red-600"><XIcon className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                {form.objetivos.length === 0 && (
                  <div className="text-xs text-gray-400 italic">Sin objetivos. Añade el primero abajo.</div>
                )}
              </div>
              <div className="flex gap-2">
                <input value={nuevoObjetivo} onChange={(e) => setNuevoObjetivo(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addObjetivo(); } }}
                  placeholder='Ej: "Empujar catering corporativo madrid a top 5"'
                  className="flex-1 px-3 py-2 text-sm border rounded-md" />
                <button type="button" onClick={addObjetivo}
                  className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50">Añadir</button>
              </div>
            </div>

            <div>
              <div className="text-xs font-medium text-gray-600 mb-1">KWs priorizadas (próximas compras)</div>
              <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
                {form.kws_priorizadas.map((k, i) => (
                  <span key={i} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs flex items-center gap-1">
                    {k}
                    <button type="button" onClick={() => rmKw(i)} className="hover:text-red-600">
                      <XIcon className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {form.kws_priorizadas.length === 0 && (
                  <span className="text-xs text-gray-400 italic">Sin KWs. Añade abajo.</span>
                )}
              </div>
              <div className="flex gap-2">
                <input value={nuevaKw} onChange={(e) => setNuevaKw(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKw(); } }}
                  placeholder="Ej: catering corporativo madrid"
                  className="flex-1 px-3 py-2 text-sm border rounded-md" />
                <button type="button" onClick={addKw}
                  className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50">Añadir</button>
              </div>
            </div>

            <Campo label="Notas del mes">
              <textarea value={form.notas} onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))}
                rows={3} className="w-full px-3 py-2 text-sm border rounded-md"
                placeholder="Notas internas: cambios de estrategia, observaciones, …" />
            </Campo>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">{error}</div>
            )}

            <DialogFooter>
              <button type="button" onClick={onClose}
                className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50">Cancelar</button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 text-sm bg-duetto-primary text-white rounded-md hover:opacity-90 disabled:opacity-50">
                {saving ? 'Guardando…' : 'Guardar plan'}
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PeriodoCard({ titulo, snap, tone = 'neutral' }) {
  const toneCls = { ok: 'border-emerald-200 bg-emerald-50', neutral: 'border-gray-200 bg-white' }[tone];
  return (
    <div className={`border rounded-md p-3 ${toneCls}`}>
      <div className="text-[11px] uppercase tracking-wide font-semibold text-gray-500 mb-2">{titulo}</div>
      <div className="text-xs space-y-1">
        <div>Posición media: <strong>{snap?.posicion_media ?? '—'}</strong></div>
        <div>Impresiones: <strong>{fmtNum(snap?.impresiones)}</strong></div>
        <div>Clicks: <strong>{fmtNum(snap?.clicks)}</strong></div>
        <div>CTR: <strong>{snap?.impresiones ? `${(snap.ctr * 100).toFixed(2)}%` : '—'}</strong></div>
        <div className="text-[10px] text-gray-400 mt-1">{snap?.rango || ''} · {snap?.dias_con_dato || 0} días con dato</div>
      </div>
    </div>
  );
}
